// src/components/sections/HomeBlogsPreview.jsx
import React, { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, useInView } from 'framer-motion'
import { FaArrowRight } from 'react-icons/fa6'
import { useAppSelector, useAppDispatch } from '../../hooks/useAppDispatch.js'
import './HomeBlogsPreview.scss'
import { fetchBlogs } from '../../redux/slices/blogsSlice.js'

export default function HomeBlogsPreview({ sectionData }) {
    const ref = useRef(null)
    const navigate = useNavigate()
    const dispatch = useAppDispatch()
    const isInView = useInView(ref, { once: true, margin: '-100px' })

    const { blogs, loading: blogsLoading } = useAppSelector((state) => state.blogs)

    useEffect(() => {
        if (blogs?.length === 0)
            dispatch(fetchBlogs())
    }, [dispatch])

    // Animation variants (same idea as v1)
    const container = {
        hidden: { opacity: 0 },
        show: {
            opacity: 1,
            transition: {
                staggerChildren: 0.2,
            },
        },
    }

    const item = {
        hidden: { y: 50, opacity: 0 },
        show: {
            y: 0,
            opacity: 1,
            transition: {
                type: 'spring',
                stiffness: 100,
                damping: 15,
                duration: 0.5,
            },
        },
    }

    const fadeIn = {
        hidden: { opacity: 0 },
        show: {
            opacity: 1,
            transition: {
                duration: 0.8,
                ease: 'easeOut',
            },
        },
    }

    // Skeleton while both section data & blogs are not ready yet
    if (!sectionData && blogsLoading) {
        return (
            <section className="blogs-section">
                <div className="blogs-container">
                    <div className="animate-pulse">
                        <div className="h-8 bg-black-200 rounded w-1/3 mx-auto mb-4"></div>
                        <div className="h-6 bg-black-200 rounded w-1/2 mx-auto mb-8"></div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {[...Array(3)].map((_, i) => (
                                <div key={i} className="h-80 bg-black-200 rounded-lg"></div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>
        )
    }

    const heading = sectionData?.heading || sectionData?.Heading
    const subheading = sectionData?.subheading

    const blogCards = blogs?.blogs?.slice(0, 3) || []

    // If nothing to show at all, bail out
    if (!heading && blogCards.length === 0 && !blogsLoading) {
        return null
    }

    return (
        <section className="blogs-section" ref={ref}>
            <div className="blogs-container">
                {/* Heading */}
                <motion.h2
                    className="blogs-heading"
                    initial="hidden"
                    animate={isInView ? 'show' : 'hidden'}
                    variants={fadeIn}
                >
                    {heading?.toUpperCase() || 'DIVE INTO THE LATEST TRENDS IN OUR BLOG'}
                </motion.h2>

                {/* Subheading */}
                {subheading && (
                    <motion.p
                        className="blogs-subheading"
                        initial="hidden"
                        animate={isInView ? 'show' : 'hidden'}
                        variants={fadeIn}
                        transition={{ delay: 0.2 }}
                    >
                        {subheading}
                    </motion.p>
                )}

                {/* Cards Grid */}
                <motion.div
                    className="blogs-grid"
                    variants={container}
                    initial="hidden"
                    animate={isInView ? 'show' : 'hidden'}
                >
                    {blogCards.map((blog, index) => (
                        <motion.div
                            key={blog.id || blog._id || blog.slug || index}
                            className="blogs-card"
                            variants={item}
                            whileHover={{ y: -10 }}
                        >
                            {/* Capsule – you can adjust source (category, tag, etc.) */}
                            {(blog.category || blog.tag || blog.hero?.type) && (
                                <div className="blogs-capsules">
                                    <span className="blogs-capsule">
                                        {blog.category || blog.tag || blog.hero?.type}
                                    </span>
                                </div>
                            )}

                            {/* Image */}
                            {blog.imageUrl || blog.hero?.imageUrl ? (
                                <div className="blogs-image">
                                    <motion.img
                                        src={blog.imageUrl || blog.image || blog?.hero?.imageUrl}
                                        alt={blog.title || blog.heading_1 || 'Blog image'}
                                        initial={{ scale: 1 }}
                                        whileHover={{ scale: 1.05 }}
                                        transition={{ duration: 0.5 }}
                                    />
                                </div>
                            ) : null}

                            {/* Content */}
                            <div className="blogs-content">
                                <h3 className="blogs-title">
                                    {blog.title || blog.heading_1 || 'Blog Title'}
                                </h3>

                                <span className="blogs-button-text">
                                    <span
                                        onClick={() => {
                                            window.scrollTo({
                                                top: 0,
                                                behavior: 'smooth',
                                            })
                                            // Safe: go to blogs listing; adjust to blog detail route if you want
                                            navigate(`/blog/${blog.slug}`)
                                        }}
                                        style={{ display: 'flex', alignItems: 'center', gap: '12px' }}
                                    >
                                        {blog.button?.name || 'Read More'} <FaArrowRight fontSize={15} />
                                    </span>
                                </span>
                            </div>
                        </motion.div>
                    ))}
                </motion.div>

                {/* CTA Button – See All Blogs */}
                <motion.button
                    className="cta-button mt-5"
                    initial="hidden"
                    animate={isInView ? 'show' : 'hidden'}
                    variants={fadeIn}
                    transition={{ delay: 0.4 }}
                    onClick={() => {
                        window.scrollTo({ top: 0, behavior: 'smooth' })
                        navigate('/blogs')
                    }}
                >
                    See All Blogs
                </motion.button>
            </div>
        </section>
    )
}
